import { memo, FunctionComponent, useState } from 'react';
import { useConfig } from '../../../../eliot-components/src/ConfigProvider/hooks';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  TextField,
} from '@material-ui/core';
import { Add } from '@material-ui/icons';
import { Org } from '../../../Orgs/types';
import ButtonProgress from '../../../../eliot-components/src/ButtonProgress/ButtonProgress';
import { useUpdateOrgMutation } from '../../../../store/api-slice';
import { useSnackbar } from 'notistack';
import { parseErrorMessage } from '../../../../eliot-components/src/utils/helpers';

interface Props {
  isOpen: boolean;
  onClose(): void;
  onUpdated(): void;
  org: Org;
}

const RestrictedDialog: FunctionComponent<Props> = memo(
  ({ isOpen, org, onClose }) => {
    const { apiBaseURL } = useConfig();
    const [updateOrg, { isLoading }] = useUpdateOrgMutation();
    const restricted = true;
    const [maxUsers, setMaxUsers] = useState(
      org.restrictedOpts ? org.restrictedOpts.maxUsers : 20
    );
    const [maxWebhooks, setMaxWebhooks] = useState(
      org.restrictedOpts ? org.restrictedOpts.maxWebhooks : 20
    );
    const [domain, setDomain] = useState(
      org.restrictedOpts && org.restrictedOpts.domain
        ? org.restrictedOpts.domain
        : []
    );
    const [domainInput, setDomainInput] = useState('');
    const [domainError, setDomainError] = useState('');
    const { enqueueSnackbar } = useSnackbar();
    const [disableBtn, setDisableBtn] = useState(true);

    const addDomain = () => {
      const regex = new RegExp(
        `^(?:[a-zA-Z0-9]+(?:-[a-zA-Z0-9]+)*\\.)+[a-zA-Z]{2,}$`
      );
      const trimmedInput = domainInput.toLowerCase().trim();

      if (
        trimmedInput &&
        regex.exec(trimmedInput) &&
        !domain.includes(trimmedInput)
      ) {
        setDomain([...domain, trimmedInput]);
        setDomainInput('');
        setDomainError('');
        setDisableBtn(false);
      } else {
        // show validation error
        if (!trimmedInput) {
          setDomainError('Domain cannot be empty');
        } else if (domain.includes(trimmedInput)) {
          setDomainError('Domain already exists');
        } else if (!regex.exec(trimmedInput)) {
          setDomainError('Invalid domain format');
        }
      }
    };

    const handleSubmit = async () => {
      const orgObj = {
        id: org.id,
        name: org.name,
        restricted: restricted,
        restrictedOpts: {
          maxUsers: maxUsers,
          maxWebhooks: maxWebhooks,
          domain: domain,
        },
      };
      updateOrg({ org: orgObj, apiBaseURL }).then((resp: any) => {
        if (!resp.error) {
          enqueueSnackbar('Organization updated');
          onClose();
          return;
        }
        enqueueSnackbar(parseErrorMessage(resp.error), { variant: 'error' });
      });
    };
    return (
      <>
        <Dialog aria-labelledby="dialog-title" open={isOpen} onClose={onClose}>
          <DialogTitle id="dialog-title">Restrictions</DialogTitle>

          <DialogContent>
            <Grid container spacing={2}>
              <Grid item sm={6} xs={12}>
                <TextField
                  margin="dense"
                  variant="outlined"
                  id="max_users"
                  label="Max Users"
                  value={maxUsers}
                  onChange={(e) => {
                    let val = parseInt(e.target.value);
                    if (!val) {
                      val = 0;
                    }
                    setDisableBtn(false);
                    setMaxUsers(val);
                  }}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <TextField
                  margin="dense"
                  variant="outlined"
                  id="max_webhooks"
                  label="Max Webhooks"
                  value={maxWebhooks}
                  onChange={(e) => {
                    let val = parseInt(e.target.value);
                    if (!val) {
                      val = 0;
                    }
                    setDisableBtn(false);
                    setMaxWebhooks(val);
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  margin="dense"
                  variant="outlined"
                  id="domain"
                  label="Domain"
                  placeholder="Enter domain (e.g., example.com)"
                  value={domainInput}
                  onChange={(e) => {
                    setDomainInput(e.target.value);
                    if (domainError) setDomainError('');
                  }}
                  error={!!domainError}
                  helperText={domainError}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Add
                          color="primary"
                          onClick={addDomain}
                          style={{ cursor: 'pointer' }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
                {domain.length > 0 && (
                  <Grid container spacing={1} style={{ marginTop: 8 }}>
                    {domain.map((d, index) => (
                      <Grid item key={index}>
                        <Chip
                          label={d}
                          onDelete={() => {
                            setDomain(domain.filter((item) => item !== d));
                            setDisableBtn(false);
                          }}
                          color="primary"
                          variant="outlined"
                        />
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
            <ButtonProgress
              type="button"
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              isLoading={isLoading}
              data-testid="submit-update"
              disabled={isLoading || disableBtn}
            >
              Submit
            </ButtonProgress>
          </DialogActions>
        </Dialog>
      </>
    );
  }
);

export default RestrictedDialog;
